import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import {
  checkUserConsent,
  getBuildingDetails,
  getContestTime,
  getContestWinnerStatus,
  getTenantDetails,
  getTenantProfileProgress,
  getTenantTransactionTenantStats,
  getTransactions,
  updateTenantDetails,
} from '../../auth/_requests';
import { useModal } from '../../helpers/hooks/modalContext';
import { usePoints } from '../../helpers/hooks/pointsContext';
import { usePointTasksContext } from '../../helpers/hooks/pointTasksContext';
import { showToast } from '../../helpers/utils/showToast';
import {
  continuePayRentSetupAfterTerms,
  openDashboardPayRentFlow,
  openPayRentFlowOrModals,
  shouldShowDashboardIdvAuthHold,
} from '../../helpers/utils/payFlowGuard';
import { shouldShowIdvChangeRequest } from '../../helpers/utils/idvStatus';
import {
  consumeStartPayRentSetup,
  saveSignupTermsConsent,
} from '../../helpers/utils/signupConsent';
import {
  buildingDetails,
  ContestWinnerStatusData,
  ContestWinnerStatusEnum,
  tenantDetails,
} from '../../helpers/utils/types';
import AccountTypeCard from './AccountType/AccountTypeCard';
import IDVMessage from './IDVMessage/IDVMessage';
import AutopayStatusBanner from './AutopayStatusBanner/AutopayStatusBanner';
import {
  ContestHero,
  ContestWinBanner,
  FeatureHighlights,
  // HomesSection,
  MetricsOverview,
  OnboardingMetrics,
  RentSetupBanner,
  StatusBanner,
  WelcomeHero,
} from './components/DashboardSections';
import './DashboardHome.css';
import { Countdown, NextPayment /* , UiProperty */ } from './types';
import { PushNotificationBootstrap } from '../../helpers/webPush/PushNotificationBootstrap';
import { pushStorage } from '../../helpers/webPush/pushNotificationStorage';
import { useDeepLinkFulfillment } from '../../helpers/hooks/useDeepLinkFulfillment';
import { DeepLinkIntent } from '../../helpers/utils/deepLink';

const Dashboard: React.FC<{ tenant?: tenantDetails }> = ({ tenant: tenantProp }) => {
  const navigate = useNavigate();
  const { auth, currentUser } = useAuth();
  const userId = currentUser?.user?.userId;
  const { modal, modalState, setModal, clearModalRefreshFlags } = useModal();
  const modalStateRef = useRef(modalState);
  modalStateRef.current = modalState;

  const [tenant, setTenant] = useState<tenantDetails | undefined>(tenantProp);
  const [loading, setLoading] = useState(true);
  const [hasMadeFirstPayment, setHasMadeFirstPayment] = useState<boolean>(() => {
    return userId != null ? pushStorage.hasMadeFirstPayment(userId) : false;
  });
  const [hasCompletedIdv, setHasCompletedIdv] = useState<boolean>(() => {
    return userId != null ? pushStorage.hasCompletedIdv(userId) : false;
  });

  useEffect(() => {
    if (userId != null) {
      if (pushStorage.hasCompletedIdv(userId)) {
        setHasCompletedIdv(true);
      }
      if (pushStorage.hasMadeFirstPayment(userId)) {
        setHasMadeFirstPayment(true);
      }
    }
  }, [userId]);
  /** After first successful/failed dashboard fetch; avoids "Refreshing…" flash on first paint */
  const initialDashboardFetchDoneRef = useRef(false);
  /** Tracks whether we have already auto-opened the change-request modal this session */
  const changeRequestModalOpenedRef = useRef(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [buildings, setBuildings] = useState<buildingDetails[]>([]);
  const [showIdvBanner, setShowIdvBanner] = useState(false);
  const [idvBannerVariant, setIdvBannerVariant] = useState<'auth-hold' | 'default'>('default');
  const [saleTransactionCount, setSaleTransactionCount] = useState(0);
  const [countdown, setCountdown] = useState<Countdown>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [contestWinnerStatus, setContestWinnerStatus] = useState<ContestWinnerStatusData | null>(
    null
  );
  const { points: pointsData, loading: pointsLoading } = usePoints();
  const { tasks: offers, loading: pointTasksLoading } = usePointTasksContext();
  const [nextPayment, setNextPayment] = useState<NextPayment>({
    amount: 0,
    property: 'N/A',
    dueText: 'N/A',
    days: 0,
  });

  useEffect(() => {
    setTenant(tenantProp);
  }, [tenantProp]);

  /**
   * Auto-open the application change-request flow when the admin has requested changes
   * and the tenant hasn't submitted them yet (timestampCompleted is null).
   * This handles both "Application only" and "Both Application and IDV" request types.
   */
  useEffect(() => {
    if (changeRequestModalOpenedRef.current) return;
    if (!tenant || modal) return;

    const tenantIdv = tenant?.tenant?.tenantIdv;
    if (!tenantIdv) return;

    const tenantIdvStatus = tenantIdv.status;
    const timestampCompleted = tenantIdv.timestampCompleted ?? null;

    if (shouldShowIdvChangeRequest({ tenantIdvStatus, timestampCompleted })) {
      changeRequestModalOpenedRef.current = true;
      const firstBuildingId = tenant?.tenantBuildings?.[0]?.tenantBuildingId || '';
      setModal('idv-change-request', {
        tenantBuildingId: firstBuildingId,
        messageFromAdmin: tenantIdv.messageFromAdmin || null,
        isNewIdvRequired: Boolean(tenantIdv.isNewIdvRequired),
      });
    }
  }, [tenant, modal, setModal]);

  useEffect(() => {
    const checkTermsConsent = async () => {
      if (!auth?.data.token) return;

      const [consentResponse, buildingsRes] = await Promise.all([
        checkUserConsent(auth.data.token),
        getBuildingDetails(auth.data.token).catch(() => ({ data: [] as buildingDetails[] })),
      ]);

      const hasProperty = Boolean(buildingsRes.data?.[0]?.building.tenantBuildingId);
      const { fromSignup, shouldOpenPaySetup } = consumeStartPayRentSetup();

      // Coming from signup: user already agreed on the form — never show T&C; open pay setup once.
      if (fromSignup) {
        if (consentResponse?.data?.isUserConsentRequired) {
          await saveSignupTermsConsent(auth.data.token);
        }
        if (shouldOpenPaySetup && !hasProperty) {
          requestAnimationFrame(() => {
            void continuePayRentSetupAfterTerms(auth.data.token, setModal, navigate);
          });
        }
        return;
      }

      if (consentResponse?.data?.isUserConsentRequired) {
        setModal('terms-services', {
          pendingPayRentSetupAfterTerms:
            modalStateRef.current?.pendingPayRentSetupAfterTerms === true || !hasProperty,
        });
      }
    };
    if (auth) checkTermsConsent();
  }, [auth, setModal, navigate]);

  const fulfillLeaseUpdateDeepLink = useCallback(
    (intent: DeepLinkIntent) => {
      if (!intent.tenantBuildingId) {
        return;
      }

      setModal('my-homes', {
        tenantBuildingId: intent.tenantBuildingId,
        leaseEditFocus: {
          rentAmount: true,
          leaseStartDate: true,
          leaseEndDate: true,
        },
      });
    },
    [setModal]
  );

  useDeepLinkFulfillment(
    'update_lease_dates',
    fulfillLeaseUpdateDeepLink,
    Boolean(auth?.data.token) && !modal
  );

  useEffect(() => {
    const fetchAll = async () => {
      if (!auth?.data.token) return;
      setLoading(true);
      setServerError(null);
      try {
        const [
          buildingsRes,
          contestTimeRes,
          contestStatusRes,
          transactionStats,
          profileRes,
          pendingTxRes,
          tenantDetailsRes,
        ] = await Promise.all([
          getBuildingDetails(auth.data.token),
          getContestTime(auth.data.token),
          getContestWinnerStatus(auth.data.token).catch(() => ({
            message: '',
            data: { hasWon: false } as ContestWinnerStatusData,
          })),
          getTenantTransactionTenantStats(auth.data.token).catch(() => null),
          getTenantProfileProgress(auth.data.token).catch(() => null),
          getTransactions(auth.data.token, { page: 1, limit: 1, status: 'pending' }).catch(
            () => null
          ),
          getTenantDetails(auth.data.token).catch(() => null),
        ]);

        setBuildings(buildingsRes.data);
        if (tenantDetailsRes?.data) {
          setTenant(tenantDetailsRes.data);
        }
        const transactionCount = transactionStats?.saleTransactionCount ?? 0;
        setSaleTransactionCount(transactionCount);
        const tenantData = tenantDetailsRes?.data?.tenant;
        const isIdentityVerified =
          Boolean(profileRes?.data?.steps?.isIdentityVerified) ||
          tenantData?.isIdentityVerified === 1 ||
          (tenantData?.isIdentityVerified as unknown) === true ||
          tenantData?.tenantIdv?.status === 'approved';
        const hasPendingTransaction = (pendingTxRes?.meta?.totalCount ?? 0) > 0;
        if (transactionCount > 0 || hasPendingTransaction) {
          setHasMadeFirstPayment(true);
          if (userId != null) {
            pushStorage.setHasMadeFirstPayment(userId);
          }
        }
        if (isIdentityVerified) {
          setHasCompletedIdv(true);
          if (userId != null) {
            pushStorage.setHasCompletedIdv(userId);
          }
        }
        setShowIdvBanner(shouldShowDashboardIdvAuthHold(isIdentityVerified));
        setIdvBannerVariant(hasPendingTransaction ? 'auth-hold' : 'default');
        setContestWinnerStatus(contestStatusRes?.data ?? { hasWon: false });

        setCountdown({
          days: Number(contestTimeRes?.data?.remainingEndTimeFromNow?.days ?? 0),
          hours: Number(contestTimeRes?.data?.remainingEndTimeFromNow?.hours ?? 0),
          minutes: Number(contestTimeRes?.data?.remainingEndTimeFromNow?.minutes ?? 0),
          seconds: Number(contestTimeRes?.data?.remainingEndTimeFromNow?.seconds ?? 0),
        });

        const first = buildingsRes.data[0];
        if (first) {
          const dueDate = new Date(first.building.nextDueDate);
          const now = new Date();
          const days = Math.max(
            0,
            Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
          );
          setNextPayment({
            amount: Number(first.building.rentAmount || 0),
            property: first.building.buildingName || 'N/A',
            dueText: dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            days,
          });
        }
      } catch (e) {
        setServerError(e instanceof Error ? e.message : 'Failed to load dashboard');
      } finally {
        initialDashboardFetchDoneRef.current = true;
        setLoading(false);
      }
    };
    fetchAll();
  }, [auth?.data.token, userId]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCountdown((prev) => {
        const total = prev.days * 86400 + prev.hours * 3600 + prev.minutes * 60 + prev.seconds;
        const next = Math.max(0, total - 1);
        return {
          days: Math.floor(next / 86400),
          hours: Math.floor((next % 86400) / 3600),
          minutes: Math.floor((next % 3600) / 60),
          seconds: next % 60,
        };
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!modalState?.refreshProperties || !auth?.data.token) return;
    const fetchBuildings = async () => {
      try {
        const res = await getBuildingDetails(auth.data.token);
        setBuildings(res.data);
      } finally {
        clearModalRefreshFlags();
      }
    };
    fetchBuildings();
  }, [modalState?.refreshProperties, auth?.data.token, clearModalRefreshFlags]);

  useEffect(() => {
    if (!modalState?.refreshProfile || !auth?.data.token) return;
    const refreshIdvBanner = async () => {
      try {
        const [profileRes, pendingTxRes, tenantDetailsRes] = await Promise.all([
          getTenantProfileProgress(auth.data.token).catch(() => null),
          getTransactions(auth.data.token, { page: 1, limit: 1, status: 'pending' }).catch(
            () => null
          ),
          getTenantDetails(auth.data.token).catch(() => null),
        ]);
        if (tenantDetailsRes?.data) {
          setTenant(tenantDetailsRes.data);
        }
        const tenantData = tenantDetailsRes?.data?.tenant;
        const isIdentityVerified =
          Boolean(profileRes?.data?.steps?.isIdentityVerified) ||
          tenantData?.isIdentityVerified === 1 ||
          (tenantData?.isIdentityVerified as unknown) === true ||
          tenantData?.tenantIdv?.status === 'approved';
        const hasPendingTransaction = (pendingTxRes?.meta?.totalCount ?? 0) > 0;
        setShowIdvBanner(shouldShowDashboardIdvAuthHold(isIdentityVerified));
        setIdvBannerVariant(hasPendingTransaction ? 'auth-hold' : 'default');
        if (isIdentityVerified) {
          setHasCompletedIdv(true);
          if (userId != null) {
            pushStorage.setHasCompletedIdv(userId);
          }
        }
      } finally {
        clearModalRefreshFlags();
      }
    };
    refreshIdvBanner();
  }, [modalState?.refreshProfile, auth?.data.token, clearModalRefreshFlags, userId]);

  const pointsBalance = Number(pointsData?.data?.points?.totalAvailable ?? 0);
  const streakValue = useMemo(
    () => offers.find((o) => o.code === 'rentPayment3MonthStreak')?.progress?.completedCount ?? 0,
    [offers]
  );
  const streakPct = (Math.min(streakValue, 6) / 6) * 100;

  const autoPayOn = Boolean(buildings[0]?.building.hasTenantMitSubscription);
  const hasProperty = Boolean(buildings[0]?.building.tenantBuildingId);
  const showOnboardingDashboard = !loading && saleTransactionCount === 0 && !hasProperty;

  /** Single spotlight property on the dashboard (first active building). */
  // const dashboardHomeProperty = useMemo<UiProperty | null>(() => {
  //   const building = buildings[0];
  //   if (!building) return null;
  //   const dueDate = building.building.nextDueDate
  //     ? new Date(building.building.nextDueDate)
  //     : new Date();
  //   const days = Math.max(0, Math.ceil((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  //   const mgr = building.tenantManager
  //     ? `${building.tenantManager.firstName} ${building.tenantManager.lastName}`.trim()
  //     : '';
  //   const addr = building.address?.fullAddress || '';
  //   const addressDisplay = mgr ? `${addr} · ${mgr}` : addr;
  //   return {
  //     id: building.building.tenantBuildingId,
  //     name: building.building.buildingName?.trim() || 'Home',
  //     unit: `Unit ${building.building.unitNumber || 'N/A'}`,
  //     address: addressDisplay,
  //     dueDate: dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  //     daysUntilDue: days,
  //     rent: Number(building.building.rentAmount || 0),
  //     streak: streakValue,
  //     color: '#0055FF',
  //     tenantBuildingId: building.building.tenantBuildingId,
  //   };
  // }, [buildings, streakValue]);

  const onNav = (
    route: 'rewards' | 'pay' | 'pay-rent' | 'homes' | 'reporting' | 'rent-drop' | 'wallet'
  ) => {
    if (route === 'pay-rent') {
      void openDashboardPayRentFlow(buildings, setModal, navigate, auth?.data.token, modalState);
      return;
    }
    if (route === 'wallet') {
      navigate('/wallet');
      return;
    }
    if (route === 'pay') {
      const activeBuilding = buildings.find((b) => b.building.isActive);
      const id = activeBuilding?.building.tenantBuildingId;
      if (!id) {
        void openDashboardPayRentFlow(buildings, setModal, navigate, auth?.data.token, modalState);
        return;
      }
      void openPayRentFlowOrModals(id, buildings, setModal, navigate, 'pay-now', 'direct');
      return;
    }
    if (route === 'homes') {
      setModal('my-homes');
      return;
    }
    if (route === 'reporting') {
      navigate('/rent-reporting');
      return;
    }
    if (route === 'rent-drop') {
      navigate('/rent-drop');
      return;
    }
    navigate('/rewards');
  };

  const changeAccountType = async () => {
    if (tenant?.tenant?.accountType === 'personal') {
      navigate('/kyb');
      return;
    }
    try {
      await updateTenantDetails(auth?.data.token, { tenantDetails: { accountType: 'both' } });
      if (auth?.data.token) {
        const response = await getTenantDetails(auth.data.token);
        setTenant(response.data);
      }
      showToast({ message: 'Account type updated successfully', type: 'success' });
    } catch (error) {
      showToast({ message: 'Failed to update account type. Please try again.', type: 'error' });
    }
  };

  const rowMotion = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  return (
    <div className={`pg-home on${showOnboardingDashboard ? ' is-onboarding' : ''}`} id='pg-home'>
      <PushNotificationBootstrap hasCompletedIdv={hasCompletedIdv} hasMadeFirstPayment={hasMadeFirstPayment} />
      <AutopayStatusBanner buildings={buildings} />
      {showIdvBanner ? <IDVMessage buildingData={buildings} variant={idvBannerVariant} /> : null}
      {contestWinnerStatus?.hasWon &&
      contestWinnerStatus.status !== ContestWinnerStatusEnum.Expired ? (
        <ContestWinBanner winnerStatus={contestWinnerStatus} />
      ) : null}

      {showOnboardingDashboard ? (
        <WelcomeHero countdown={countdown} countdownPending={loading} onNav={onNav} />
      ) : (
        <ContestHero
          tickets={Number(pointsData?.data?.tickets?.totalAvailable ?? 0)}
          ticketsPending={pointsLoading}
          countdown={countdown}
          countdownPending={loading}
          onNav={onNav}
        />
      )}
      <motion.div
        className='pg-home-rows'
        initial='hidden'
        animate='show'
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.12, delayChildren: 0.02 } },
        }}
      >
        {showOnboardingDashboard ? (
          <motion.div variants={rowMotion}>
            <OnboardingMetrics onNav={onNav} />
          </motion.div>
        ) : (
          <>
            {!loading && !hasProperty ? (
              <motion.div variants={rowMotion}>
                <RentSetupBanner onSetup={() => onNav('pay-rent')} />
              </motion.div>
            ) : null}

            <motion.div variants={rowMotion}>
              <MetricsOverview
                nextPayment={nextPayment}
                pointsBalance={pointsBalance}
                streakValue={streakValue}
                streakPct={streakPct}
                autoPayOn={autoPayOn}
                onNav={onNav}
                pendingNextPayment={loading}
                pendingPoints={pointsLoading}
                pendingStreak={pointTasksLoading}
                isFirstRent={saleTransactionCount === 0}
              />
            </motion.div>
          </>
        )}

        <motion.div variants={rowMotion}>
          <FeatureHighlights onNav={onNav} onRefer={() => navigate('/rewards/referrals')} />
        </motion.div>

        {/* <motion.div variants={rowMotion}>
          <HomesSection
            property={dashboardHomeProperty}
            ticketsAvailable={Number(pointsData?.data?.tickets?.totalAvailable ?? 0)}
            ticketsPending={pointsLoading}
            onNav={onNav}
            onPay={(tenantBuildingId) =>
              void openPayRentFlowOrModals(
                tenantBuildingId,
                buildings,
                setModal,
                navigate,
                'pay-rent'
              )
            }
          />
        </motion.div> */}

        {tenant && tenant?.tenant?.accountType !== 'both' ? (
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 16 },
              show: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] },
              },
            }}
          >
            <StatusBanner
              accountTypeSection={
                <AccountTypeCard
                  accountType={tenant.tenant.accountType}
                  onChangeAccountType={changeAccountType}
                  tenant={tenant}
                />
              }
              serverError={serverError}
              showRefreshingNotice={
                !showOnboardingDashboard && loading && initialDashboardFetchDoneRef.current
              }
            />
          </motion.div>
        ) : serverError ||
          (!showOnboardingDashboard && loading && initialDashboardFetchDoneRef.current) ? (
          <motion.div variants={rowMotion}>
            <StatusBanner
              accountTypeSection={null}
              serverError={serverError}
              showRefreshingNotice={
                !showOnboardingDashboard && loading && initialDashboardFetchDoneRef.current
              }
            />
          </motion.div>
        ) : null}
      </motion.div>
    </div>
  );
};

export default Dashboard;
